import {
  formatSekMinor,
  type SubscriptionEndRefundRow,
} from "@care-by-ella/contracts";

// TES-1554 (Ö-861, Ö-863) — one row of "Avslut och återbetalning" as the
// table shows it. Pure, so the wording is testable without a DOM.

/** Stripe's status, as admin needs it to see whether the end is done. */
export function endRefundSubscriptionLabel(status: string): string {
  switch (status) {
    case "trialing":
      return "Provperiod pågår";
    case "active":
      return "Aktivt";
    case "past_due":
    case "unpaid":
      return "Aktivt, betalning misslyckades";
    case "paused":
      return "Pausat";
    case "canceled":
    case "incomplete_expired":
      return "Avslutat";
    default:
      return status;
  }
}

/** "15 okt–14 nov" — an exclusive period end shown as its last paid day. */
function paidDays(startDate: string, endDateExclusive: string): string {
  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString("sv-SE", {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
    });
  const last = new Date(`${endDateExclusive}T12:00:00Z`);
  last.setUTCDate(last.getUTCDate() - 1);
  return `${fmt(startDate)}–${fmt(last.toISOString().slice(0, 10))}`;
}

export interface EndRefundView {
  taskId: string;
  patientId: string;
  name: string;
  endDate: string;
  subscription: string;
  stripeSubscriptionId: string;
  refund: string;
  // One line per paid period the refund reaches into.
  working: string[];
  // Set when a period was charged after the task was made.
  newCharge: string | null;
  open: boolean;
  closedAt: string | null;
}

export function endRefundViewRow(r: SubscriptionEndRefundRow): EndRefundView {
  const refund =
    r.refundMinor === null
      ? "Okänt – betalt belopp för perioden finns inte hos oss. Läs det i Stripe."
      : formatSekMinor(r.refundMinor);
  const working = r.lines.map(
    (l) =>
      `${paidDays(l.periodStartDate, l.periodEndDate)}: ${formatSekMinor(
        l.amountPaidMinor,
      )} betalt, ${l.refundDays} av ${l.periodDays} dagar = ${formatSekMinor(
        l.refundMinor,
      )}`,
  );
  const newCharge =
    r.currentRefundMinor !== null && r.currentRefundMinor !== r.refundMinor
      ? `Beräknat i dag: ${formatSekMinor(
          r.currentRefundMinor,
        )}. En period har dragits efter att uppgiften skapades och ska också återbetalas.`
      : null;
  return {
    taskId: r.taskId,
    patientId: r.patientId,
    name: r.patientName ?? `Patient …${r.patientId.slice(-6)}`,
    endDate: r.endDate,
    subscription: endRefundSubscriptionLabel(r.subscriptionStatus),
    stripeSubscriptionId: r.stripeSubscriptionId,
    refund,
    working,
    newCharge,
    open: r.closedAt === null,
    closedAt: r.closedAt ? r.closedAt.slice(0, 10) : null,
  };
}
